import { compareGoals } from '@mona-roadmap/domain';
import type { Deliverable, Epic, ProductGoal, Task } from '@mona-roadmap/domain';
import { ChevronDown, ExternalLink, Plus } from 'lucide-react';
import { useMemo, useState } from 'react';
import { fieldKey, useFieldState } from '../../lib/fieldState';
import { formatDate, formatTime } from '../../lib/format';
import { isTempId } from '../../lib/optimistic';
import { useTasks } from '../../lib/queries';
import { DateField, PeopleField, SelectField } from '../cells/Editors';
import { useRoadmapContext } from '../RoadmapContext';
import type { OptionItem } from '../ui/OptionList';
import { SingleSelect } from '../ui/Pickers';
import { FieldStatusIcon } from '../ui/Visuals';
import { Field, FieldError, Sheet, TextCommitField } from './parts';

interface Props {
  deliverable: Deliverable;
  goals: ProductGoal[];
  epics: Epic[];
  onClose: () => void;
  onCloseFocus: () => void;
}

export function DeliverableDrawer({ deliverable: d, goals, epics, onClose, onCloseFocus }: Props) {
  const ctx = useRoadmapContext();
  const { metadata, mutations } = ctx;
  const key = (f: string) => fieldKey('d', d.id, f);
  const pending = isTempId(d.id);
  /** Relationship change = move: after saving, reveal the row at its new place. */
  const moveTo = async (changes: { productGoalId: string | null } | { epicId: string | null }) => {
    const r = await mutations.patchDeliverable(d.id, changes);
    if (r.ok) ctx.reveal({ kind: 'deliverable', id: d.id, focus: false });
  };
  const goalId = d.productGoalIds[0] ?? null;
  const epicId = d.epicIds[0] ?? null;

  const goalItems = useMemo<OptionItem[]>(() => [...goals].sort(compareGoals).map((g) => ({ value: g.id, label: g.name, hint: g.targetDate ? formatDate(g.targetDate) : undefined })), [goals]);
  const epicItems = useMemo<OptionItem[]>(() => [...epics].sort((a, b) => a.position - b.position).map((e) => ({ value: e.id, label: e.name, hint: e.productArea ?? undefined })), [epics]);

  return (
    <Sheet
      title={d.name}
      subtitle={d.key ? `Deliverable · ${d.key}` : 'Deliverable'}
      onClose={onClose}
      onCloseFocus={onCloseFocus}
      footer={
        <div className="flex items-center justify-between gap-2">
          <span>{d.updatedAt ? `Updated ${formatDate(d.updatedAt.slice(0, 10))} ${formatTime(d.updatedAt)}` : ''}</span>
          {d.url ? (
            <a className="inline-flex items-center gap-1 text-accent-ink hover:underline" href={d.url} target="_blank" rel="noopener noreferrer">
              Open in monday <ExternalLink className="size-3.5" aria-hidden />
            </a>
          ) : null}
        </div>
      }
    >
      <TextCommitField label="Name" value={d.name} stateKey={key('name')} disabled={pending} onCommit={(name) => void mutations.patchDeliverable(d.id, { name })} />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field label="Product Goal" note="Clear to make it a standalone / no-goal deliverable.">
          <RelationPicker
            label="Product Goal"
            value={goalId}
            items={goalItems}
            emptyText="No Product Goal"
            stateKey={key('productGoalId')}
            disabled={pending}
            onChange={(v) => void moveTo({ productGoalId: v })}
          />
          <FieldError stateKey={key('productGoalId')} />
        </Field>
        <Field label="Epic" note="Clear to make it a standalone deliverable.">
          <RelationPicker label="Epic" value={epicId} items={epicItems} emptyText="Standalone (no epic)" stateKey={key('epicId')} disabled={pending} onChange={(v) => void moveTo({ epicId: v })} />
          <FieldError stateKey={key('epicId')} />
        </Field>
      </div>

      <Field label="Product Area" note="Inherited from Epic (read-only). Change it on the Epic.">
        <div className="flex h-8 items-center rounded-md border border-line bg-raised px-2 text-dim" title="Inherited from Epic (read-only)">
          {d.productArea ?? <span className="text-faint">Not set</span>}
        </div>
      </Field>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field label="PM Owner">
          <PeopleField block field="PM Owner" people={d.pmOwners} all={metadata.people} stateKey={key('pmOwnerIds')} readOnly={pending} onCommit={(ids) => void mutations.patchDeliverable(d.id, { pmOwnerIds: ids })} />
          <FieldError stateKey={key('pmOwnerIds')} />
        </Field>
        <Field label="Assignee">
          <PeopleField block field="Assignee" people={d.assignees} all={metadata.people} stateKey={key('assigneeIds')} readOnly={pending} onCommit={(ids) => void mutations.patchDeliverable(d.id, { assigneeIds: ids })} />
          <FieldError stateKey={key('assigneeIds')} />
        </Field>
        <Field label="Status">
          <SelectField block field="Status" style="chip" value={d.status} options={metadata.options.status} stateKey={key('status')} readOnly={pending} onChange={(v) => void mutations.patchDeliverable(d.id, { status: v })} clearLabel="No status" />
          <FieldError stateKey={key('status')} />
        </Field>
        <Field label="Sprint">
          <SelectField block field="Sprint" style="sprint" value={d.sprint} options={metadata.options.sprint} sprintColors={ctx.sprintColors} stateKey={key('sprint')} readOnly={pending} onChange={(v) => void mutations.patchDeliverable(d.id, { sprint: v })} clearLabel="No sprint" />
          <FieldError stateKey={key('sprint')} />
        </Field>
        <Field label="Scoping Status">
          <SelectField block field="Scoping Status" style="chip" value={d.scopingStatus} options={metadata.options.scopingStatus} stateKey={key('scopingStatus')} readOnly={pending} onChange={(v) => void mutations.patchDeliverable(d.id, { scopingStatus: v })} clearLabel="No scoping status" />
          <FieldError stateKey={key('scopingStatus')} />
        </Field>
        <Field label="Scoping Due Date">
          <DateField block field="Scoping Due Date" value={d.scopingDue} stateKey={key('scopingDue')} readOnly={pending} onChange={(v) => void mutations.patchDeliverable(d.id, { scopingDue: v })} />
          <FieldError stateKey={key('scopingDue')} />
        </Field>
      </div>

      <TasksSection deliverable={d} disabled={pending} />
    </Sheet>
  );
}

function RelationPicker({
  label,
  value,
  items,
  emptyText,
  stateKey,
  disabled,
  onChange,
}: {
  label: string;
  value: string | null;
  items: OptionItem[];
  emptyText: string;
  stateKey: string;
  disabled?: boolean;
  onChange: (v: string | null) => void;
}) {
  const state = useFieldState(stateKey);
  const current = items.find((i) => i.value === value);
  if (disabled) return <div className="flex h-8 items-center rounded-md border border-line bg-raised px-2 text-dim">{current?.label ?? emptyText}</div>;
  return (
    <SingleSelect
      label={label}
      items={items}
      value={value}
      searchable
      clearLabel={emptyText}
      onChange={onChange}
      trigger={
        <button
          type="button"
          className={`cell-btn !m-0 !h-8 w-full !justify-between !border-line-strong !bg-app !px-2 ${state?.status === 'error' ? 'fs-error' : ''} ${state?.status === 'saved' ? 'fs-saved' : ''}`}
          aria-label={`${label}: ${current?.label ?? emptyText}. Change`}
          disabled={state?.status === 'saving'}
        >
          <span className={`truncate ${current ? '' : 'italic text-faint'}`}>{current?.label ?? emptyText}</span>
          <span className="flex items-center gap-1">
            <FieldStatusIcon state={state} />
            <ChevronDown className="size-3.5 text-faint" aria-hidden />
          </span>
        </button>
      }
    />
  );
}

// ---- tasks (monday subitems) ---------------------------------------------------------------------------------

function TasksSection({ deliverable, disabled }: { deliverable: Deliverable; disabled: boolean }) {
  const { mutations } = useRoadmapContext();
  const { data: tasks, isLoading, error, refetch } = useTasks(deliverable.id, !disabled);
  const [name, setName] = useState('');

  const add = () => {
    const n = name.trim();
    if (!n) return;
    setName('');
    void mutations.createTask(deliverable.id, { name: n });
  };

  return (
    <section aria-label="Tasks" className="space-y-2">
      <div className="flex items-center justify-between border-t border-line pt-3">
        <h3 className="field-label">Tasks {tasks ? `(${tasks.length})` : ''}</h3>
        <span className="text-[12.5px] text-faint">monday subitems</span>
      </div>
      {isLoading ? (
        <div className="space-y-1.5" aria-busy>
          <div className="skeleton h-7" />
          <div className="skeleton h-7" />
        </div>
      ) : error ? (
        <p className="text-[14px] text-danger" role="alert">
          Could not load tasks.{' '}
          <button type="button" className="underline" onClick={() => void refetch()}>
            Retry
          </button>
        </p>
      ) : tasks && tasks.length === 0 ? (
        <p className="text-[14px] text-faint">No tasks yet.</p>
      ) : (
        <ul className="space-y-1.5">{tasks?.map((t) => <TaskRow key={t.id} task={t} />)}</ul>
      )}
      <form
        className="flex items-center gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          add();
        }}
      >
        <input className="input" placeholder="Add a task..." aria-label="New task name" value={name} maxLength={255} disabled={disabled} onChange={(e) => setName(e.target.value)} />
        <button type="submit" className="btn" disabled={disabled || !name.trim()}>
          <Plus className="size-3.5" aria-hidden /> Add
        </button>
      </form>
    </section>
  );
}

function TaskRow({ task }: { task: Task }) {
  const { metadata, mutations } = useRoadmapContext();
  const key = (f: string) => fieldKey('t', task.id, f);
  const pending = isTempId(task.id);
  return (
    <li className="grid grid-cols-[1fr_auto_auto] items-center gap-2 rounded-md border border-line bg-raised px-2 py-1" data-testid="task-row">
      <TextCommitField compact label={`Task name ${task.name}`} value={task.name} stateKey={key('name')} disabled={pending} onCommit={(n) => void mutations.patchTask(task, { name: n })} />
      <span className="px-1">
        <SelectField field="Task status" style="chip" value={task.status} options={metadata.options.taskStatus} stateKey={key('status')} readOnly={pending} onChange={(v) => void mutations.patchTask(task, { status: v })} clearLabel="No status" />
      </span>
      <PeopleField field="Task owner" people={task.owners} all={metadata.people} stateKey={key('ownerIds')} readOnly={pending} onCommit={(ids) => void mutations.patchTask(task, { ownerIds: ids })} />
    </li>
  );
}
